import { Component, ChangeDetectionStrategy, OnDestroy, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { firstValueFrom, Subscription, switchMap } from 'rxjs';
import { CurrencyInputComponent } from '../product-editor/currency-input.component';
import { ProductService } from '../../services/products.service';
import { ConfirmationService, LoadingComponent } from '@digi-america/shared';
import { PriceCode } from '../../types/PriceCode';

type CodeType = 'BASE' | 'BULK';

interface PriceCodeRowForm {
    type: FormControl<'list' | 'direct' | 'dealer' | null>;
    codeType: FormControl<CodeType>;
    bulkQty: FormControl<number | null>;
    code: FormControl<string>; // computed from codeType/bulkQty; kept readonly in UI
    price: FormControl<number | null>;
}

@Component({
    selector: 'app-product-price-editor',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, CurrencyInputComponent, LoadingComponent],
    templateUrl: './product-price.component.html'
})
export class ProductPriceEditorComponent implements OnInit {
    private fb = inject(FormBuilder);
    private route = inject(ActivatedRoute);
    private productService = inject(ProductService);
    private confirmation = inject(ConfirmationService);
    private router = inject(Router);

    productCategory: string = "";
    productName: string = "";
    productId: string | null = null;
    priceCodes: PriceCode[] | null = null;

    form = this.fb.group({
        priceCodes: this.fb.array<FormGroup<PriceCodeRowForm>>([]),
    });


    get priceCodesFA(): FormArray<FormGroup<PriceCodeRowForm>> {
        return this.form.get('priceCodes') as FormArray<FormGroup<PriceCodeRowForm>>;
    }

    async ngOnInit() {
        this.productId = (await firstValueFrom(this.route.paramMap)).get("id");
        if (this.productId) {
            let productPromise = firstValueFrom(this.productService.getProduct(this.productId));
            this.priceCodes = await firstValueFrom(this.productService.productPrice(this.productId));
            let product = await productPromise;

            this.productCategory = product.type;
            this.productName = `${product.sku}: ${product.name}`;

            this.priceCodesFA.clear();
            this.priceCodes.forEach((c) => this.priceCodesFA.push(this.buildRowFromExisting(c)));
        }
    }


    // ---------- Row builders & helpers ----------

    private buildRowFromExisting(existing: PriceCode): FormGroup<PriceCodeRowForm> {
        const { codeType, bulkQty } = this.splitCode(existing.code);
        const group = this.buildEmptyRow(codeType, bulkQty);

        group.patchValue({
            type: existing.type,
            price: existing.price,
        });

        // ensure code string is computed initially
        this.recomputeCode(group);

        return group;
    }

    private buildEmptyRow(initialCodeType: CodeType = 'BASE', initialBulkQty: number | null = null): FormGroup<PriceCodeRowForm> {
        const group = this.fb.group<PriceCodeRowForm>({
            type: this.fb.control<'list' | 'direct' | 'dealer'>('list', { validators: [Validators.required] }),
            codeType: this.fb.control<CodeType>(initialCodeType, { nonNullable: true }),
            bulkQty: this.fb.control<number | null>(initialCodeType === 'BULK' ? initialBulkQty ?? 1 : null, {
                validators: [],
            }),
            code: this.fb.control<string>('', { nonNullable: true }),
            price: this.fb.control<number | null>(null, { validators: [Validators.required, Validators.min(0.01)] }),
        });

        // react to codeType/bulkQty changes to recompute code string
        const sub = group.valueChanges.subscribe(() => this.recomputeCode(group));
        // store subscription on the group for cleanup if needed
        (group as any).__sub = sub;

        return group;
    }

    private splitCode(code: string): { codeType: CodeType; bulkQty: number | null } {
        // Allowed inputs:
        // "BASE" or "BULK(n)" where n is a positive integer
        if (code?.startsWith('BULK(') && code.endsWith(')')) {
            const inner = code.slice(5, -1);
            const qty = Number(inner);
            if (!Number.isNaN(qty) && qty > 0) {
                return { codeType: 'BULK', bulkQty: qty };
            }
        }
        return { codeType: 'BASE', bulkQty: null };
    }

    private recomputeCode(group: FormGroup<PriceCodeRowForm>): void {
        const codeType = group.controls.codeType.value;
        const qty = group.controls.bulkQty.value;

        let finalCode = 'BASE';
        if (codeType === 'BULK') {
            const n = Number(qty ?? 0);
            finalCode = n > 0 ? `BULK(${n})` : 'BULK(1)'; // default to 1 if invalid/empty
        }
        group.controls.code.setValue(finalCode, { emitEvent: false });

        // Maintain validators on bulkQty based on codeType
        if (codeType === 'BULK') {
            group.controls.bulkQty.addValidators([Validators.required, Validators.min(1)]);
            group.controls.bulkQty.updateValueAndValidity({ emitEvent: false });
        } else {
            group.controls.bulkQty.clearValidators();
            group.controls.bulkQty.setValue(null, { emitEvent: false });
            group.controls.bulkQty.updateValueAndValidity({ emitEvent: false });
        }
    }

    // ---------- UI actions ----------

    addRow(): void {
        this.priceCodesFA.push(this.buildEmptyRow('BASE', null));
    }

    async removeRow(index: number): Promise<void> {
        const confirmed = await this.confirmation.confirm({
            message: 'Remove this price code?',
            confirmText: 'Remove',
            cancelText: 'Cancel',
            confirmButtonType: 'danger'
        });
        if (confirmed) {
            const group = this.priceCodesFA.at(index) as any;
            group?.__sub?.unsubscribe?.();
            this.priceCodesFA.removeAt(index);
        }
    }

    getValue(): PriceCode[] {
        return this.priceCodesFA.controls.map((g) => ({
            type: g.controls.type.value!,
            code: g.controls.code.value!,
            price: Number(g.controls.price.value ?? 0),
        }));
    }

    async save() {
        if (this.form.invalid || !this.productId) {
            this.form.markAllAsTouched();
            return;
        }
        const payload = this.getValue();
        await firstValueFrom(this.productService.saveProductPrice(this.productId, this.getValue()));
        
        this.router.navigate(["/", "product-search", this.productCategory]);
    }

    trackByIndex = (_: number, __: unknown) => _;
}
